import { Link } from 'react-router';
import { Button } from '@dzseguidores/ui';
import type { ProductPublic } from '@dzseguidores/types';
import { ProductCard } from '@/components/cro/product-card';
import { useCartStore } from '@/stores/cart-store';
import { PriceBlock } from '@/components/cro/price-block';
import { pickOrderBump } from '@/lib/cro';

type FeaturedProps = {
  products: ProductPublic[];
  isLoading?: boolean;
  title?: string;
  intro?: string;
  limit?: number;
  showAllLink?: boolean;
  networkFilter?: string;
  onNetworkChange?: (slug: string) => void;
  networks?: Array<{ slug: string; name: string }>;
};

export function FeaturedCatalog({
  products,
  isLoading,
  title = 'Pacotes prontos para comprar',
  intro = 'Escolha seguidores ou curtidas e finalize com PIX em minutos.',
  limit = 6,
  showAllLink = true,
  networkFilter = '',
  onNetworkChange,
  networks = [],
}: FeaturedProps) {
  const list = products.slice(0, limit);

  return (
    <section id="pacotes" className="scroll-mt-24">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div className="max-w-2xl">
          <h2 className="text-2xl font-semibold sm:text-3xl">{title}</h2>
          <p className="mt-2 text-muted">{intro}</p>
        </div>
        {showAllLink && (
          <Link to={networkFilter ? `/catalogo?network=${networkFilter}` : '/catalogo'}>
            <Button variant="secondary" size="sm">
              Ver catálogo completo
            </Button>
          </Link>
        )}
      </div>

      {onNetworkChange && networks.length > 0 && (
        <div className="mt-6 flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => onNetworkChange('')}
            className={`rounded-lg border px-3 py-1.5 text-sm font-medium transition ${
              !networkFilter
                ? 'border-[var(--color-accent)] bg-[var(--color-accent)] text-[var(--color-accent-fg)]'
                : 'border-border bg-surface text-fg hover:bg-bg'
            }`}
          >
            Todos
          </button>
          {networks.map((n) => (
            <button
              key={n.slug}
              type="button"
              onClick={() => onNetworkChange(n.slug)}
              className={`rounded-lg border px-3 py-1.5 text-sm font-medium transition ${
                networkFilter === n.slug
                  ? 'border-[var(--color-accent)] bg-[var(--color-accent)] text-[var(--color-accent-fg)]'
                  : 'border-border bg-surface text-fg hover:bg-bg'
              }`}
            >
              {n.name}
            </button>
          ))}
        </div>
      )}

      {isLoading && <p className="mt-8 text-muted">Carregando pacotes…</p>}

      {!isLoading && list.length === 0 && (
        <p className="mt-8 rounded-xl border border-border p-4 text-sm text-muted">
          Nenhum pacote disponível no momento.{' '}
          <Link to="/catalogo" className="underline hover:text-fg">
            Ver catálogo
          </Link>
        </p>
      )}

      <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {list.map((product, i) => (
          <ProductCard key={product.id} product={product} peers={products} index={i} />
        ))}
      </div>
    </section>
  );
}

type OrderBumpProps = {
  catalog: ProductPublic[];
};

export function OrderBumpOffer({ catalog }: OrderBumpProps) {
  const items = useCartStore((s) => s.items);
  const addItem = useCartStore((s) => s.addItem);
  const bump = pickOrderBump(
    items.map((i) => i.productId),
    catalog,
  );

  if (!bump || items.length === 0) return null;

  const already = items.some((i) => i.productId === bump.id);
  if (already) return null;

  return (
    <label className="mt-6 flex cursor-pointer gap-3 rounded-xl border border-border p-4 transition hover:border-fg/25">
      <input
        type="checkbox"
        className="mt-1 h-4 w-4 accent-[var(--color-accent)]"
        onChange={(e) => {
          if (e.target.checked) {
            addItem({
              productId: bump.id,
              slug: bump.slug,
              name: bump.name,
              priceCents: bump.promoPriceCents ?? bump.priceCents,
              quantity: 1,
            });
          }
        }}
      />
      <div className="min-w-0 flex-1">
        <p className="text-sm font-semibold text-fg">Leve também com desconto de pacote</p>
        <p className="mt-1 text-sm text-muted">{bump.name}</p>
        <PriceBlock
          className="mt-2"
          priceCents={bump.priceCents}
          promoPriceCents={bump.promoPriceCents}
          size="sm"
        />
      </div>
    </label>
  );
}

type CrossSellProps = {
  product: ProductPublic | null;
};

export function CrossSellCard({ product }: CrossSellProps) {
  if (!product) return null;
  return (
    <div className="rounded-xl border border-border p-5">
      <p className="text-xs font-semibold uppercase tracking-wide text-muted">
        Quem comprou também levou
      </p>
      <h3 className="mt-2 font-semibold text-fg">
        <Link to={`/produto/${product.slug}`} className="hover:underline">
          {product.name}
        </Link>
      </h3>
      <p className="mt-1 line-clamp-2 text-sm text-muted">{product.description}</p>
      <div className="mt-4 flex items-center justify-between gap-3">
        <PriceBlock
          priceCents={product.priceCents}
          promoPriceCents={product.promoPriceCents}
          size="sm"
        />
        <Link to={`/produto/${product.slug}`}>
          <Button size="sm">Ver pacote</Button>
        </Link>
      </div>
    </div>
  );
}
